import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { sectionRenderer, buildSectionSelector, morphSection } from '@theme/section-renderer';
import { requestIdleCallback } from '@theme/utilities';

class ProductSwatchesComponent extends Component {
  /** @type {Set<string>} Option names the user has manually interacted with */
  #userSelectedOptions = new Set();

  /** @type {string[] | null} Cached sibling section IDs inside <main> */
  #siblingSectionIds = null;

  connectedCallback() {
    super.connectedCallback();
    this.#preloadSwatches();
    this.#listenForUserSelection();
    this.#initRovingTabindex();
  }

  /**
   * Listens for user clicks on variant radios to track which options were manually chosen
   */
  #listenForUserSelection() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    section.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;

      const fieldset = event.target.closest('.variant-option');
      const legend = fieldset?.querySelector('legend');
      if (legend) {
        this.#userSelectedOptions.add(legend.textContent.trim());
      }
    });
  }

  /**
   * Initializes roving tabindex keyboard navigation on swatches.
   * Arrow keys move focus between swatches, Enter/Space activates.
   */
  #initRovingTabindex() {
    this.addEventListener('keydown', (event) => {
      const swatches = /** @type {HTMLAnchorElement[]} */ ([...this.querySelectorAll('.product-swatches__swatch')]);
      if (!swatches.length) return;

      const currentIndex = swatches.indexOf(/** @type {HTMLAnchorElement} */ (document.activeElement));
      if (currentIndex === -1) return;

      if (event.key === ' ') {
        event.preventDefault();
        swatches[currentIndex]?.click();
        return;
      }

      let nextIndex;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (currentIndex + 1) % swatches.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = (currentIndex - 1 + swatches.length) % swatches.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = swatches.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      swatches[currentIndex]?.setAttribute('tabindex', '-1');
      swatches[nextIndex]?.setAttribute('tabindex', '0');
      swatches[nextIndex]?.focus();
      swatches[nextIndex]?.click();
    });
  }

  /**
   * Returns the IDs of all `.shopify-section` elements inside `<main>` other
   * than the one hosting this component. The list is stable across swatch
   * swaps (we morph contents, never add/remove sections), so it's memoized.
   * @returns {string[]}
   */
  #getSiblingSectionIds() {
    if (this.#siblingSectionIds) return this.#siblingSectionIds;

    const main = document.querySelector('main');
    const currentId = this.closest('.shopify-section')?.id?.replace('shopify-section-', '');
    const ids = [];

    if (main && currentId) {
      for (const section of main.querySelectorAll('.shopify-section')) {
        const id = section.id?.replace('shopify-section-', '');
        if (id && id !== currentId) ids.push(id);
      }
    }

    this.#siblingSectionIds = ids;
    return ids;
  }

  /**
   * Preloads swatch sections into the cache during idle time. Main product
   * section is warmed first; sibling sections in `<main>` are warmed in a
   * second idle pass so they never compete with the critical fetch.
   */
  #preloadSwatches() {
    const sectionId = this.closest('.shopify-section')?.id?.replace('shopify-section-', '');
    if (!sectionId) return;

    const links = /** @type {HTMLAnchorElement[]} */ (
      [...this.querySelectorAll('a:not([aria-current="true"])')].filter((l) => l instanceof HTMLAnchorElement)
    );

    requestIdleCallback(() => {
      for (const link of links) {
        sectionRenderer.getSectionHTML(sectionId, true, new URL(link.href));
      }

      const siblingIds = this.#getSiblingSectionIds();
      if (!siblingIds.length) return;

      requestIdleCallback(() => {
        for (const link of links) {
          for (const id of siblingIds) {
            sectionRenderer.getSectionHTML(id, true, new URL(link.href));
          }
        }
      });
    });
  }

  /**
   * Captures the currently selected option values keyed by option name,
   * only for options the user has manually interacted with
   * @returns {Map<string, string>}
   */
  #captureSelectedOptions() {
    const selected = new Map();
    if (!this.#userSelectedOptions.size) return selected;

    const section = this.closest('.shopify-section');
    if (!section) return selected;

    for (const fieldset of section.querySelectorAll('.variant-option')) {
      const legend = fieldset.querySelector('legend');
      const name = legend?.textContent?.trim() ?? '';
      if (!this.#userSelectedOptions.has(name)) continue;

      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked instanceof HTMLInputElement) {
        selected.set(name, checked.value);
      }
    }

    return selected;
  }

  /**
   * Applies previously selected options to the new variant picker if they match an available variant
   * @param {Map<string, string>} previousOptions
   */
  #applyPreviousOptions(previousOptions) {
    if (!previousOptions.size) return;

    const section = this.closest('.shopify-section');
    if (!section) return;

    const variantPicker = section.querySelector('variant-picker');
    if (!variantPicker) return;

    const variantsJson = variantPicker.querySelector('script[type="application/json"]')?.textContent;
    if (!variantsJson) return;

    /** @type {{ available?: boolean, options?: string[] }[]} */
    let variants;
    try {
      variants = JSON.parse(variantsJson);
    } catch {
      return;
    }

    const fieldsets = [...variantPicker.querySelectorAll('.variant-option')];
    const optionNames = fieldsets.map((f) => f.querySelector('legend')?.textContent?.trim() ?? '');

    // Build candidate options: use previous value if option name matches, otherwise keep default (checked)
    const candidateValues = optionNames.map((name, index) => {
      if (previousOptions.has(name)) return previousOptions.get(name);
      const checked = fieldsets[index]?.querySelector('input[type="radio"]:checked');
      return checked instanceof HTMLInputElement ? checked.value : '';
    });

    // Check if a variant with these exact options exists and is available
    const match = variants.find(
      (v) => v.available && v.options?.length === candidateValues.length && v.options.every((o, i) => o === candidateValues[i])
    );

    if (!match) return;

    // Apply the matched options
    for (let i = 0; i < fieldsets.length; i++) {
      const value = candidateValues[i] ?? '';
      const radio = fieldsets[i]?.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
      if (radio instanceof HTMLInputElement && !radio.disabled) {
        radio.checked = true;
      }
    }

    // Trigger change so variant picker updates price, URL, etc.
    variantPicker.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Handles swatch click — fetches the target product section, preloads
   * the first media image, then morphs everything except the media area
   * (which is swapped atomically with pre-decoded content, like product-card).
   * @param {Event} event
   */
  handleSwatchClick = async (event) => {
    event.preventDefault();

    const target = /** @type {Element | null} */ (event.target);
    const link = /** @type {HTMLAnchorElement | null} */ (target?.closest('a'));
    if (!link || link.getAttribute('aria-current') === 'true') return;

    const productUrl = link.href;
    const sectionId = this.closest('.shopify-section')?.id?.replace('shopify-section-', '');

    if (!sectionId) return;

    const previousOptions = this.#captureSelectedOptions();

    this.#setLoading(link);
    this.#showMediaBlur();

    const url = new URL(productUrl);

    // Fire sibling-section fetches in parallel. They morph independently as
    // they arrive — never blocking the main section swap, which is the only
    // change the user cares about seeing instantly.
    for (const id of this.#getSiblingSectionIds()) {
      sectionRenderer
        .getSectionHTML(id, true, url)
        .then(async (html) => {
          await morphSection(id, html);
          this.#rehydrateAppSection(id);
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;
          console.warn(`Sibling section ${id} swap failed:`, error);
        });
    }

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, true, url);

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const selector = buildSectionSelector(sectionId);
      const existingSection = document.getElementById(selector);
      const newSection = doc.getElementById(selector);

      if (!existingSection || !newSection) throw new Error('Section not found');

      // Preload first image offscreen before touching the DOM
      const newMedia = newSection.querySelector('.product-media');
      await this.#preloadFirstImage(newMedia);

      // Skip media subtree during morph so the blur stays
      const currentMedia = existingSection.querySelector('.product-media');
      currentMedia?.setAttribute('data-skip-subtree-update', '');
      newMedia?.setAttribute('data-skip-subtree-update', '');

      morph(existingSection, newSection, { ...MORPH_OPTIONS });

      // Swap media atomically with pre-decoded content (same as product-card)
      if (currentMedia && newMedia) {
        currentMedia.replaceChildren(...newMedia.childNodes);
      }
      currentMedia?.removeAttribute('data-skip-subtree-update');

      this.#hideMediaBlur();

      // Swap the URL before re-applying the options: the variant picker appends
      // `?variant=` to the current URL when it handles the change event, so it
      // has to run against the new product URL instead of being overwritten by it.
      window.history.replaceState({}, '', productUrl);

      this.#applyPreviousOptions(previousOptions);
    } catch (error) {
      this.#hideMediaBlur();
      console.error('Product swatch navigation failed:', error);
      window.location.href = productUrl;
    }
  };

  /**
   * Re-hydrates third-party app sections whose scripts hydrate once at page
   * load. Judge.me injects a script in <head> that scans the DOM for its
   * widgets and removes placeholder styles — after an AJAX section swap its
   * observer misses the new markup, so we clone & re-insert the script to
   * trigger a fresh scan. The browser serves it from cache, so it's cheap.
   * @param {string} sectionId
   */
  #rehydrateAppSection(sectionId) {
    if (sectionId === 'judgeme' || sectionId.endsWith('__judgeme')) {
      // The Judge.me loader is guarded by `jdgm._loaderExecuted` and
      // dedups loaded widget bundles via `jdgm.loadScript.requestedUrls`.
      // Clear both so the cloned <script> re-runs its widget scan — `V(e)`
      // inside the loader is what flips `.jdgm-legacy-widget-content` from
      // `display:none` back to visible.
      const jdgm = /** @type {any} */ (/** @type {any} */ (window).jdgm);
      if (jdgm) {
        jdgm._loaderExecuted = false;
        if (jdgm.loadScript?.requestedUrls) jdgm.loadScript.requestedUrls.length = 0;
      }
      this.#reexecuteHeadScripts((src) => src.includes('judge.me') || src.includes('judgeme'));
    }
  }

  /**
   * Replaces matching `<script src>` tags in <head> with fresh clones so the
   * browser re-executes them.
   * @param {(src: string) => boolean} match
   */
  #reexecuteHeadScripts(match) {
    for (const script of document.head.querySelectorAll('script[src]')) {
      if (!(script instanceof HTMLScriptElement)) continue;
      if (!match(script.src)) continue;
      const clone = document.createElement('script');
      for (const attr of script.attributes) clone.setAttribute(attr.name, attr.value);
      script.replaceWith(clone);
    }
  }

  /**
   * Adds a blur filter on `.product-media` to signal loading.
   */
  #showMediaBlur() {
    const media = this.closest('.shopify-section')?.querySelector('.product-media');
    if (media instanceof HTMLElement) {
      media.style.filter = 'blur(4px)';
      media.style.transition = 'filter 200ms ease';
    }
  }

  /**
   * Removes the blur filter from `.product-media`.
   */
  #hideMediaBlur() {
    const media = this.closest('.shopify-section')?.querySelector('.product-media');
    if (media instanceof HTMLElement) {
      media.style.filter = '';
      media.style.transition = '';
    }
  }

  /**
   * Inserts the first image from parsed media content into an offscreen
   * container and waits for it to decode before returning.
   * @param {Element | null} mediaElement
   */
  async #preloadFirstImage(mediaElement) {
    if (!mediaElement) return;

    const firstImg = /** @type {HTMLImageElement | null} */ (
      mediaElement.querySelector('.product-media-slider__slide:first-child img')
    );
    if (!firstImg) return;

    const preload = document.createElement('div');
    preload.style.cssText = 'position:fixed;left:-9999px;width:1px;height:1px;overflow:hidden;pointer-events:none';

    const img = /** @type {HTMLImageElement} */ (firstImg.cloneNode(true));
    img.loading = 'eager';
    preload.appendChild(img);
    document.body.appendChild(preload);

    try {
      await Promise.race([
        img.decode(),
        new Promise((_, reject) => setTimeout(reject, 3000)),
      ]);
    } catch {
      // Timeout or decode error — proceed anyway
    }

    preload.remove();
  }

  /**
   * Adds a loading state to the clicked swatch
   * @param {HTMLElement} activeLink
   */
  #setLoading(activeLink) {
    for (const swatch of this.querySelectorAll('.product-swatches__swatch')) {
      swatch.classList.remove('product-swatches__swatch--active');
      swatch.setAttribute('aria-checked', 'false');
      swatch.setAttribute('tabindex', '-1');
    }

    activeLink.classList.add('product-swatches__swatch--active');
    activeLink.setAttribute('aria-checked', 'true');
    activeLink.setAttribute('tabindex', '0');
    activeLink.setAttribute('aria-busy', 'true');
  }
}

if (!customElements.get('product-swatches-component')) {
  customElements.define('product-swatches-component', ProductSwatchesComponent);
}
